import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { BarChart3, Clock, Package, RefreshCw, Trash2 } from 'lucide-react'
import { useDeleteAuditProduct } from '@/hooks/useAmazonMutations'

interface ProductItem {
  id: string
  audit_product_id?: string
  asin: string
  tld: string
  status: string
  created_at: string | null
  scores?: { overall: number }
  product_title?: string | null
  image_url?: string | null
  audit_count?: number
  is_new?: boolean
  remake_status?: 'none' | 'pending' | 'running' | 'completed' | 'failed'
}

const STATUS_COLORS: Record<string, string> = {
  pending: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
  scraping: 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400',
  analyzing: 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400',
  completed: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  failed: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  partial: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
  running: 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400',
}

const REMAKE_STATUS_COLORS: Record<string, string> = {
  none: 'bg-neutral-100 text-neutral-500 dark:bg-neutral-700 dark:text-neutral-400',
  pending: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
  running: 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400',
  completed: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  failed: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
}

const TERMINAL_STATUSES = ['completed', 'failed', 'partial']

function useStatusLabel(status: string | null, t: (key: string) => string): string {
  if (!status) return t('amazonListing.status.pending')
  if (TERMINAL_STATUSES.includes(status)) return t(`amazonListing.status.${status}`)
  if (status === 'pending') return t('amazonListing.status.pending')
  return t('amazonListing.status.running')
}

function statusColor(status: string | null): string {
  if (!status || status === 'pending') return STATUS_COLORS.pending
  if (TERMINAL_STATUSES.includes(status)) return STATUS_COLORS[status] ?? STATUS_COLORS.pending
  return STATUS_COLORS.running
}

export function AmazonProductCard({ product }: { product: ProductItem }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const statusLabel = useStatusLabel(product.status, t)
  const statusCls = statusColor(product.status)
  const deleteProduct = useDeleteAuditProduct()

  const scoreColor = (s: number) => s >= 80 ? 'text-green-500' : s >= 60 ? 'text-yellow-500' : 'text-red-500'

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => navigate(`/v2/tools/product-listing-audit/${product.audit_product_id ?? product.id}`)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(`/v2/tools/product-listing-audit/${product.audit_product_id ?? product.id}`) } }}
      className="w-full text-left p-4 bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-xl hover:border-orange-300 dark:hover:border-orange-600 hover:shadow-md transition-all group cursor-pointer"
    >
      <div className="flex gap-3">
        <div className="shrink-0 w-16 h-16 rounded-lg overflow-hidden bg-neutral-100 dark:bg-neutral-700 flex items-center justify-center">
          {product.image_url
            ? <img src={product.image_url} alt={product.product_title || product.asin} className="w-full h-full object-contain" />
            : <Package className="h-6 w-6 text-neutral-400" />}
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2 mb-1">
            <span className="px-2 py-0.5 bg-neutral-100 dark:bg-neutral-700 rounded text-xs font-mono font-medium text-neutral-600 dark:text-neutral-300">
              {product.asin}
            </span>
            <div className="flex items-center gap-1 shrink-0">
              {product.is_new && (
                <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-orange-500 text-white animate-pulse">
                  {t('amazonListing.status.new')}
                </span>
              )}
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusCls}`}>
                {statusLabel}
              </span>
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${REMAKE_STATUS_COLORS[product.remake_status ?? 'none'] ?? REMAKE_STATUS_COLORS.none}`}>
                <RefreshCw className="h-3 w-3" />
                {t(`amazonListing.remakeStatus.${product.remake_status ?? 'none'}`)}
              </span>
              <button onClick={(e) => { e.stopPropagation(); if (window.confirm(t('amazonListing.deleteConfirm'))) deleteProduct.mutate(product.audit_product_id ?? product.id) }}
                className="p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity text-neutral-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20">
                <Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          </div>

          {product.product_title && (
            <p className="text-sm font-medium text-neutral-800 dark:text-neutral-200 truncate mb-1">
              {product.product_title}
            </p>
          )}

          <div className="flex items-center gap-2 text-xs text-neutral-500 dark:text-neutral-400">
            <span className="uppercase font-medium">{t(`amazonListing.marketplaces.${product.tld}`)}</span>
            {product.created_at && (
              <>
                <span>·</span>
                <Clock className="h-3 w-3" />
                <span>{new Date(product.created_at).toLocaleDateString()}</span>
              </>
            )}
            {product.audit_count && product.audit_count > 1 && (
              <span className="px-1.5 py-0.5 bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 rounded text-xs font-medium">
                {t('amazonListing.versionCount', { count: product.audit_count })}
              </span>
            )}
          </div>
        </div>
      </div>

      {product.scores?.overall != null && (
        <div className="flex items-center gap-2 mt-3 pt-3 border-t border-neutral-100 dark:border-neutral-700">
          <BarChart3 className="h-4 w-4 text-neutral-400" />
          <span className="text-sm text-neutral-500">{t('amazonListing.report.overallScore')}:</span>
          <span className={`text-lg font-bold ${scoreColor(product.scores.overall)}`}>{product.scores.overall}</span>
          <span className="text-xs text-neutral-400">/100</span>
        </div>)}
    </div>
  )
}
